import { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import { ImagePicker } from "./ImagePicker";
import { api } from "../lib/api";
import { CaretUp, CaretDown, Trash, Copy, Plus, Image as ImageIcon } from "@phosphor-icons/react";

const BLOCK_DEFS = [
  { type: "logo", label: "Logo" },
  { type: "hero", label: "Hero" },
  { type: "heading", label: "Heading" },
  { type: "text", label: "Text" },
  { type: "image", label: "Image" },
  { type: "button", label: "Button" },
  { type: "columns", label: "2 columns" },
  { type: "quote", label: "Quote" },
  { type: "divider", label: "Divider" },
  { type: "spacer", label: "Spacer" },
];

const defaultsFor = (type, brand) => ({
  logo: {},
  hero: { headline: "A headline that wows", subtext: "One sentence that makes them want to read on.", button_label: "Learn more", button_url: "https://", bg_color: brand, text_color: "#ffffff", image_url: "" },
  heading: { text: "Section heading", level: "h2", align: "left" },
  text: { html: "<p>Write something compelling for {{first_name}}…</p>" },
  image: { image_url: "", alt: "", width_pct: 50, link: "", radius: 0 },
  button: { label: "Call to action", url: "https://", bg_color: brand, align: "center" },
  columns: { left_html: "<p>Left column</p>", right_html: "<p>Right column</p>" },
  quote: { text: "This changed how we work.", author: "A happy client" },
  divider: {},
  spacer: { height: 24 },
}[type] || {});

const Inp = (props) => <input {...props} className={`h-8 w-full rounded-md border border-zinc-200 px-2 text-xs ${props.className || ""}`}/>;
const Lbl = ({ children }) => <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">{children}</span>;

function ImageField({ value, onChange, onPick }) {
  return (
    <div className="flex items-center gap-2">
      {value ? <img src={value} alt="" className="h-9 w-9 object-contain border border-zinc-200 rounded bg-white"/> : null}
      <button onClick={onPick} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50" data-testid="pick-image-btn">
        <ImageIcon size={12}/> {value ? "Change image" : "Choose image"}
      </button>
      {value && <button onClick={() => onChange("")} className="text-[10px] text-zinc-400 hover:text-rose-600 underline">remove</button>}
    </div>
  );
}

function PubLinkSelect({ value, onPick, pubs }) {
  if (!pubs?.length) return null;
  const current = (value || "").includes("/p/") ? (value.split("/p/")[1] || "").split("?")[0] : "";
  return (
    <div>
      <Lbl>Link to gated publication</Lbl>
      <select data-testid="pub-link-select" value={current} onChange={e => { if (e.target.value) onPick(`${window.location.origin}/p/${e.target.value}`); }}
        className="h-8 w-full rounded-md border border-zinc-200 text-xs px-1">
        <option value="">— choose to auto-fill link —</option>
        {pubs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
      </select>
    </div>
  );
}

function BlockFields({ block, patch, openPicker, pubs }) {
  const t = block.type;
  if (t === "logo") return (
    <div className="flex items-center gap-3">
      <p className="text-[11px] text-zinc-500 flex-1">Uses your brand logo from Settings automatically.</p>
      <div className="w-40">
        <Lbl>Size ({block.width || 200}px)</Lbl>
        <input data-testid="logo-width" type="range" min="100" max="300" step="10" value={block.width || 200} onChange={e => patch({ width: Number(e.target.value) })} className="w-full"/>
      </div>
    </div>
  );
  if (t === "hero") {
    const heroBtnOn = !!((block.button_label || "").trim() || ((block.button_url || "").trim() && !["https://", "http://"].includes((block.button_url || "").trim())));
    return (
    <div className="space-y-2">
      <div><Lbl>Headline</Lbl><Inp value={block.headline || ""} onChange={e => patch({ headline: e.target.value })} data-testid="hero-headline"/></div>
      <div><Lbl>Subtext</Lbl><Inp value={block.subtext || ""} onChange={e => patch({ subtext: e.target.value })}/></div>
      <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
        <input data-testid="hero-btn-toggle" type="checkbox" checked={heroBtnOn} onChange={e => patch(e.target.checked ? { button_label: "Learn more", button_url: "https://" } : { button_label: "", button_url: "" })} className="h-3.5 w-3.5 accent-zinc-900"/>
        Include a CTA button
      </label>
      {heroBtnOn && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div><Lbl>Button label</Lbl><Inp value={block.button_label || ""} onChange={e => patch({ button_label: e.target.value })}/></div>
            <div><Lbl>Button link</Lbl><Inp value={block.button_url || ""} onChange={e => patch({ button_url: e.target.value })}/></div>
          </div>
          <PubLinkSelect value={block.button_url} onPick={(url) => patch({ button_url: url })} pubs={pubs}/>
        </>
      )}
      <div className="flex items-center gap-4">
        <div><Lbl>Background</Lbl><input type="color" value={block.bg_color || "#005191"} onChange={e => patch({ bg_color: e.target.value })} className="h-8 w-12 rounded border border-zinc-200 cursor-pointer"/></div>
        <div className="flex-1"><Lbl>Top image (optional)</Lbl><ImageField value={block.image_url} onChange={v => patch({ image_url: v })} onPick={() => openPicker("image_url")}/></div>
      </div>
    </div>
    );
  }
  if (t === "heading") return (
    <div className="grid grid-cols-3 gap-2">
      <div className="col-span-2"><Lbl>Text</Lbl><Inp value={block.text || ""} onChange={e => patch({ text: e.target.value })} data-testid="heading-text"/></div>
      <div><Lbl>Size / align</Lbl>
        <div className="flex gap-1">
          <select value={block.level || "h2"} onChange={e => patch({ level: e.target.value })} className="h-8 rounded-md border border-zinc-200 text-xs px-1"><option value="h1">H1</option><option value="h2">H2</option></select>
          <select value={block.align || "left"} onChange={e => patch({ align: e.target.value })} className="h-8 rounded-md border border-zinc-200 text-xs px-1"><option>left</option><option>center</option></select>
        </div>
      </div>
    </div>
  );
  if (t === "text") return <div data-testid="text-block-editor"><ReactQuill theme="snow" value={block.html || ""} onChange={v => patch({ html: v })}/></div>;
  if (t === "image") return (
    <div className="space-y-2">
      <ImageField value={block.image_url} onChange={v => patch({ image_url: v })} onPick={() => openPicker("image_url")}/>
      <div className="grid grid-cols-3 gap-2">
        <div><Lbl>Width %</Lbl><Inp type="number" min={20} max={100} value={block.width_pct ?? 100} onChange={e => patch({ width_pct: Number(e.target.value) })}/></div>
        <div><Lbl>Corner radius</Lbl><Inp type="number" min={0} max={30} value={block.radius ?? 0} onChange={e => patch({ radius: Number(e.target.value) })}/></div>
        <div><Lbl>Link (optional)</Lbl><Inp value={block.link || ""} onChange={e => patch({ link: e.target.value })}/></div>
      </div>
    </div>
  );
  if (t === "button") return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-2 items-end">
        <div><Lbl>Label</Lbl><Inp value={block.label || ""} onChange={e => patch({ label: e.target.value })} data-testid="button-label"/></div>
        <div><Lbl>Link</Lbl><Inp value={block.url || ""} onChange={e => patch({ url: e.target.value })}/></div>
        <div><Lbl>Colour</Lbl><input type="color" value={block.bg_color || "#005191"} onChange={e => patch({ bg_color: e.target.value })} className="h-8 w-12 rounded border border-zinc-200 cursor-pointer"/></div>
        <div><Lbl>Align</Lbl><select value={block.align || "center"} onChange={e => patch({ align: e.target.value })} className="h-8 w-full rounded-md border border-zinc-200 text-xs px-1"><option>left</option><option>center</option></select></div>
      </div>
      <PubLinkSelect value={block.url} onPick={(url) => patch({ url, label: (!block.label || block.label === "Call to action") ? "Request the full article" : block.label })} pubs={pubs}/>
    </div>
  );
  if (t === "columns") return (
    <div className="grid grid-cols-2 gap-2">
      <div><Lbl>Left</Lbl><ReactQuill theme="snow" value={block.left_html || ""} onChange={v => patch({ left_html: v })}/></div>
      <div><Lbl>Right</Lbl><ReactQuill theme="snow" value={block.right_html || ""} onChange={v => patch({ right_html: v })}/></div>
    </div>
  );
  if (t === "quote") return (
    <div className="grid grid-cols-3 gap-2">
      <div className="col-span-2"><Lbl>Quote</Lbl><Inp value={block.text || ""} onChange={e => patch({ text: e.target.value })}/></div>
      <div><Lbl>Author</Lbl><Inp value={block.author || ""} onChange={e => patch({ author: e.target.value })}/></div>
    </div>
  );
  if (t === "spacer") return <div className="w-32"><Lbl>Height (px)</Lbl><Inp type="number" min={4} max={120} value={block.height ?? 24} onChange={e => patch({ height: Number(e.target.value) })}/></div>;
  return null;
}

export function EmailBuilder({ blocks, onChange, settings, disabled }) {
  const [picker, setPicker] = useState(null); // {index, field}
  const [pubs, setPubs] = useState([]);
  const brand = settings?.brand_color || "#005191";

  useEffect(() => { api.listPublications().then(d => setPubs(d.items || [])).catch(() => {}); }, []);

  const setBlocks = (b) => onChange(b);
  const patchAt = (i, p) => setBlocks(blocks.map((b, j) => j === i ? { ...b, ...p } : b));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const remove = (i) => setBlocks(blocks.filter((_, j) => j !== i));
  const duplicate = (i) => setBlocks([...blocks.slice(0, i + 1), { ...blocks[i] }, ...blocks.slice(i + 1)]);
  const add = (type) => setBlocks([...blocks, { type, ...defaultsFor(type, brand) }]);

  return (
    <div className="space-y-2" data-testid="email-builder">
      {blocks.length === 0 && (
        <div className="text-center text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg py-8">
          Start designing — add your first block below, or use <strong>AI design</strong>.
        </div>
      )}
      {blocks.map((b, i) => (
        <div key={i} className="rounded-lg border border-zinc-200 bg-white" data-testid={`builder-block-${i}`}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-100 bg-zinc-50/60 rounded-t-lg">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{BLOCK_DEFS.find(d => d.type === b.type)?.label || b.type}</span>
            {!disabled && (
              <div className="flex items-center gap-0.5">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-zinc-400 hover:text-zinc-900 disabled:opacity-30" data-testid={`block-up-${i}`}><CaretUp size={13}/></button>
                <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="p-1 text-zinc-400 hover:text-zinc-900 disabled:opacity-30" data-testid={`block-down-${i}`}><CaretDown size={13}/></button>
                <button onClick={() => duplicate(i)} className="p-1 text-zinc-400 hover:text-zinc-900" data-testid={`block-dup-${i}`}><Copy size={13}/></button>
                <button onClick={() => remove(i)} className="p-1 text-zinc-400 hover:text-rose-600" data-testid={`block-del-${i}`}><Trash size={13}/></button>
              </div>
            )}
          </div>
          <div className={`p-3 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
            <BlockFields block={b} patch={(p) => patchAt(i, p)} openPicker={(field) => setPicker({ index: i, field })} pubs={pubs}/>
          </div>
        </div>
      ))}
      {!disabled && (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid="add-block-row">
          {BLOCK_DEFS.map(d => (
            <button key={d.type} onClick={() => add(d.type)} data-testid={`add-block-${d.type}`}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-zinc-200 bg-white hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-colors">
              <Plus size={11}/> {d.label}
            </button>
          ))}
        </div>
      )}
      {picker && (
        <ImagePicker
          onSelect={(url) => { patchAt(picker.index, { [picker.field]: url }); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
